import { OnboardingStep } from '@reelplicate/api-client/model';

/**
 * The onboarding stages (Figma Onboarding / Top bar stepper) and their
 * routes. Step 1 lives at /onboarding, the others at /onboarding/<path>.
 * `step` is the saved progress (User.onboardingStep) each screen stands for.
 */
export const ONBOARDING_STEPS = [
  { label: 'Workspace', path: '', step: OnboardingStep.WORKSPACE },
  { label: 'Brand', path: 'brand', step: OnboardingStep.BRAND },
  { label: 'Competitors', path: 'competitors', step: OnboardingStep.COMPETITORS },
  { label: 'Platforms', path: 'platforms', step: OnboardingStep.PLATFORMS },
  { label: 'Plan', path: 'plan', step: OnboardingStep.PLAN },
  { label: 'Team', path: 'team', step: OnboardingStep.TEAM },
] as const;

/**
 * Payment (Figma 5b) sits under the Plan stage: the stepper and the progress
 * gate read the first segment, "plan".
 */
export const PAYMENT_PATH = '/onboarding/plan/payment';

/** Screens after the six stages; the stepper shows every stage complete. */
export const FINISH_PATHS = {
  source: 'source',
  settingUp: 'setting-up',
  done: 'done',
} as const;

/** Past the last stage: every step complete (Figma Top bar Step=Done). */
export const ALL_DONE = ONBOARDING_STEPS.length + 1;

/** The saved steps in order, as the API orders them. */
const ORDER = Object.values(OnboardingStep);

export const stepIndex = (step: OnboardingStep) => ORDER.indexOf(step);

export function stepPath(step: number) {
  const path = ONBOARDING_STEPS[step - 1]?.path;
  return path ? `/onboarding/${path}` : '/onboarding';
}

export function finishPath(key: keyof typeof FINISH_PATHS) {
  return `/onboarding/${FINISH_PATHS[key]}`;
}

/** Where someone with this saved progress picks up. */
export function pathForProgress(step: OnboardingStep) {
  if (step === OnboardingStep.SOURCE) return finishPath('source');
  if (step === OnboardingStep.COMPLETED) return finishPath('done');
  const index = ONBOARDING_STEPS.findIndex((s) => s.step === step);
  return stepPath(index + 1);
}

/** The 1-based stepper position a URL shows (ALL_DONE past the stages). */
export function stepFromPath(pathname: string) {
  const slug = pathname.split('/')[2]?.toLowerCase() ?? '';
  if ((Object.values(FINISH_PATHS) as string[]).includes(slug)) return ALL_DONE;
  const index = ONBOARDING_STEPS.findIndex((step) => step.path === slug);
  return index === -1 ? 1 : index + 1;
}

/**
 * The saved step a URL needs: its own step for the stages and "How did you
 * find us", COMPLETED for the screens after finishing.
 */
export function requiredProgress(pathname: string): OnboardingStep {
  const slug = pathname.split('/')[2]?.toLowerCase() ?? '';
  if (slug === FINISH_PATHS.source) return OnboardingStep.SOURCE;
  if (slug === FINISH_PATHS.settingUp || slug === FINISH_PATHS.done) {
    return OnboardingStep.COMPLETED;
  }
  return ONBOARDING_STEPS.find((s) => s.path === slug)?.step ?? OnboardingStep.WORKSPACE;
}
